import { Location } from 'history';
import { useInjection } from 'inversify-react';
import { debounce } from 'lodash';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { Home } from '../../../routes/config/Home';
import { NotificationLevel, ToastNotification } from '../../notifications/models/notification.model';
import { INotificationsService } from '../../notifications/services/notifications.service';
import { DatasetDetailsPreviewHeader } from '../components/DatasetDetailsPreviewHeader';
import { PublishDatasetNavigateAwayConfirmationModal } from '../components/PublishDatasetNavigateAwayConfirmationModal';
import { DatasetDetails } from '../DatasetDetails.context';
import { Datasets } from '../datasets.context';
import { useDatasetPermissions } from '../datasets.permissions';

export const DatasetDetailsPreviewHeaderContainer = observer((): ReactElement => {
  const notificationsService = useInjection(INotificationsService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const datasetsPermissions = useDatasetPermissions();
  const history = useHistory();
  const { datasetId, imageId } = useParams<{ datasetId: string; imageId: string }>();
  const context = useContext(DatasetDetails);
  const datasetsContext = useContext(Datasets);
  const detailsContext = useContext(DatasetDetails);

  const [imageIdState, setImageId] = useState(imageId);
  const [total, setTotal] = useState(0);
  const [name, setName] = useState('');
  const [current, setCurrent] = useState(0);
  const [previousId, setPreviousId] = useState('');
  const [nextId, setNextId] = useState('');
  const [imageSize, setImageSize] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const [createdDate, setCreatedDate] = useState('');
  const [hasAnnotation, setHasAnnotation] = useState(false);
  const [hasLock, setHasLock] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [nameStatus, setNameStatus] = useState(InputStatus.empty());
  const [newName, setNewName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [navigationLocation, setNavigationLocation] = useState<Location | null>(null);

  const details = detailsContext.store.details;

  const fetchData = async (): Promise<void> => {
    if (context.store.details.id !== datasetId) {
      await context.service.getDatasetDetailsAsync(datasetId);
    }
    await showImageAsync(datasetId, imageId);
  };

  useEffect(() => {
    void fetchData();
  }, [datasetId, imageId]);

  const handleImageDownload = async (): Promise<void> => {
    const result = await context.service.downloadImageAsync(imageId);

    if (result instanceof ACDLError) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'cant_download_image'));
    }
  };

  const toggleDeleteImageModal = (): void => {
    setIsDeleteModalShown((prev) => !prev);
  };

  const handleDeleteImage = async (): Promise<void> => {
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;
    const result = await context.service.deleteImageAsync(imageId);
    if (result instanceof ACDLError) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'cant_delete_image'));
    } else {
      if (previousId) {
        history.push(Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId: datasetId, imageId: previousId }));
      } else if (nextId) {
        history.push(Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId: datasetId, imageId: nextId }));
      } else {
        history.push(Home.Datasets.Details.Images.withParams({ workspaceId, datasetId: datasetId }));
      }

      setIsDeleteModalShown(false);
      datasetsContext.store.refreshFromDetails(context.store.details);
    }
  };

  const validateName = async (imageName: string): Promise<InputStatus> => {
    const inputStatus = await context.service.validateImageNameAsync(imageName, imageIdState, datasetId);
    setNameStatus(inputStatus);
    return inputStatus;
  };

  const validateNameDebounced = debounce(async (imageName: string) => {
    await validateName(imageName);
  }, 300);

  const handleImageNameBlurAsync = async (): Promise<void> => {
    const validatedNameStatus = await validateName(newName);

    if (validatedNameStatus.isValid === true) {
      const result = await context.service.renameImageAsync(imageIdState, newName, datasetId);

      if (result instanceof ACDLError) {
        setNameStatus(InputStatus.buildFrom(result.apiErrorResponse?.errorCodes ?? [])); // L1
        setNewName(name);
      } else {
        setNameStatus(InputStatus.empty());
        setName(newName);
      }
    }
  };

  const handleNameChange = (imageName: string): void => {
    setNewName(imageName);
    void validateNameDebounced(imageName);
  };

  const handleCancelImageRename = (): void => {
    setNewName(name);
    setNameStatus(InputStatus.empty());
  };

  const showImageAsync = async (datasetId: string, imageId: string): Promise<void> => {
    const store = context.store;

    runInAction(() => {
      store.isPreviewImageLoading = true;
      store.lowQualityImagePreviewUrl = '';
      store.imagePreviewUrl = '';
    });

    const [paging, image] = await Promise.all([
      context.service.getPreviewPaging(datasetId, store.previewPaging.checkDate),
      context.service.getDatasetImageInfoAsync(datasetId, imageId),
    ]);

    if (image instanceof ACDLError || paging instanceof ACDLError) {
      return;
    }

    const oldPaging = { ...store.previewPaging };
    if (paging.wasChanged) {
      runInAction(() => {
        store.previewPaging.checkDate = paging.checkDate;
        store.previewPaging.items = paging.items;
        store.previewPaging.checkDate = paging.checkDate;
      });
    }

    const workspaceId = currentWorkspaceStore.currentWorkspace.id;
    let currentIndex = 1;
    const current = store.previewPaging.items.find((x) => x.imageId === image.id);
    if (current !== undefined) {
      currentIndex = current.index;
    } else {
      const oldcurrent = oldPaging.items.find((x) => x.imageId === image.id);
      if (oldcurrent !== undefined) {
        const newImage = store.previewPaging.items.find((x) => x.index === oldcurrent.index);
        if (newImage !== undefined) {
          history.push(Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId, imageId: newImage.imageId }));
          return;
        }
        if (store.previewPaging.items.length > 0) {
          const newImage = store.previewPaging.items[store.previewPaging.items.length - 1];
          history.push(Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId, imageId: newImage.imageId }));
          return;
        }
      }
      history.push(Home.Datasets.Details.Images.withParams({ workspaceId, datasetId }));
      return;
    }

    const next = store.previewPaging.items.find((x) => x.index === currentIndex + 1);
    const previous = store.previewPaging.items.find((x) => x.index === currentIndex - 1);

    await context.service.showImageAsync(datasetId, imageId, image.size);

    const imageUsageInfo = await context.service.getImageUsageInfo(datasetId, imageId);
    const hasAnnotation = imageUsageInfo instanceof ACDLError ? false : imageUsageInfo.hasAnnotation;

    setHasAnnotation(hasAnnotation);
    setHasLock(hasLock);
    setTotal(store.previewPaging.items.length);
    setName(image.name);
    setCurrent(currentIndex);
    setPreviousId(previous ? previous.imageId : '');
    setNextId(next ? next.imageId : '');
    setImageSize(image.size);
    setImageHeight(image.height);
    setImageWidth(image.width);
    setCreatedDate(image.createdDate);
    setNewName(image.name);
    setNameStatus(InputStatus.empty());
    setImageId(image.id);
  };

  const store = context.service.store;
  const workspaceId = currentWorkspaceStore.currentWorkspace.id;

  const handleBlockedNavigation = (location: Location): boolean => {
    if (
      location.pathname.includes('/datasets/') &&
      (location.pathname.includes('/preview/') || location.pathname.includes('/images/')) &&
      location.pathname.includes(details.id)
    ) {
      return true;
    }
    setNavigationLocation(location);
    setShowModal(true);
    return false;
  };

  const onLeaveConfirm = async (): Promise<void> => {
    await detailsContext.service.publishDatasetAsync(details.id);
    if (navigationLocation) {
      history.push(navigationLocation);
    }
    setShowModal(false);
  };

  const handleKeepDraft = (): void => {
    setShowModal(false);
    if (navigationLocation) {
      history.push(navigationLocation);
    }
  };

  const handleCancel = (): void => {
    setShowModal(false);
    setNavigationLocation(null);
  };

  return (
    <>
      <PublishDatasetNavigateAwayConfirmationModal
        intercept={details.status === 'Draft' && !showModal && datasetsPermissions.canPublishDataset().allowed}
        onBlockedNavigation={handleBlockedNavigation}
        showModal={showModal}
        onConfirm={onLeaveConfirm}
        onKeepDraft={handleKeepDraft}
        onCancel={handleCancel}
      />
      <DatasetDetailsPreviewHeader
        isLoading={store.isPreviewImageLoading}
        current={current}
        name={name}
        total={total}
        imageSize={imageSize}
        channels={store.imageAttributes.channels || 0}
        createdDate={createdDate}
        imageHeight={imageHeight}
        imageWidth={imageWidth}
        isCached={store.imageAttributes.isCached || false}
        downloadSpeed={store.imageAttributes.downloadSpeed}
        isHighResImageLoading={store.imagePreviewUrl === ''}
        closeButtonLink={Home.Datasets.Details.Images.withParams({
          workspaceId,
          datasetId: datasetId,
          page: store.detailsImagesPaging.pageNumber,
        })}
        nextLink={Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId, imageId: nextId })}
        previousLink={Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId, imageId: previousId })}
        datasetName={context.service.store.details.name}
        datasetStatus={context.service.store.details.status}
        isDeleteModalShown={isDeleteModalShown}
        onDownloadImage={handleImageDownload}
        onToggleDeleteModal={toggleDeleteImageModal}
        onDeleteImage={handleDeleteImage}
        hasAnnotations={hasAnnotation}
        isLocked={hasLock}
        onImageNameBlur={handleImageNameBlurAsync}
        onImageNameChange={handleNameChange}
        nameStatus={nameStatus}
        newName={newName}
        onCancelImageRename={handleCancelImageRename}
      />
    </>
  );
});
